import { http, unwrap, type ActionResponse } from './http';
import { mapBackendAuthResult, mapBackendUser } from './auth';
import type {
  AuthSession,
  ChangePasswordInput,
  ProfileUpdateInput,
} from '@features/profile/types';
import type {
  AdminUser,
  AuthSession as TokenSession,
  BackendAuthResultDto,
  BackendUserProfileDto,
} from '@features/auth/types';

export const profileApi = {
  // BE: PUT /api/v1/auth/me — body { fullName, avatarUrl } → ActionResponse<UserProfileDto>.
  // BE treats `AvatarUrl is null` as "don't touch this field" and `string.IsNullOrWhiteSpace(...)`
  // as "clear". We therefore send `""` (not `null`) when the caller wants to remove the avatar
  // so the BE actually clears it; an undefined avatarUrl preserves the existing value.
  async update(input: ProfileUpdateInput): Promise<AdminUser> {
    const res = await http.put<ActionResponse<BackendUserProfileDto>>('/api/v1/auth/me', {
      fullName: input.fullName,
      avatarUrl: input.avatarUrl === undefined ? null : input.avatarUrl ?? '',
    });
    return mapBackendUser(unwrap(res));
  },

  // BE: POST /api/v1/auth/change-password — body { currentPassword, newPassword }.
  // BE revokes all prior sessions but re-issues a fresh one for this device, so
  // the caller stays signed in. We return the new session for the auth store.
  async changePassword(input: ChangePasswordInput): Promise<TokenSession> {
    const res = await http.post<ActionResponse<BackendAuthResultDto>>('/api/v1/auth/change-password', {
      currentPassword: input.currentPassword,
      newPassword: input.newPassword,
    });
    return mapBackendAuthResult(unwrap(res));
  },

  // BE: GET /api/v1/auth/sessions — active refresh-token sessions for the current user.
  async listSessions(): Promise<AuthSession[]> {
    const res = await http.get<ActionResponse<AuthSession[]>>('/api/v1/auth/sessions');
    return unwrap(res) ?? [];
  },
  // BE: DELETE /api/v1/auth/sessions/{id} — revoke one session's refresh-token chain.
  async revokeSession(id: string): Promise<void> {
    await http.delete(`/api/v1/auth/sessions/${id}`);
  },
};
